import React from "react";
import { DependencyList, useCallback, useEffect, useState } from "react";
import { useUserManager } from "../EnsureLogin";
import { IfComp } from "@dvrt/react-tools";
import { ListSubheader } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { Item } from "./shared";

type UsePromise<T> = {
    /// Indicates if the promise is currently loading
    loading: boolean;

    /// The data returned by the promise, if resolved
    data?: T;

    /// The error returned by the promise, if rejected
    error?: Error;

    /// Indicates if the promise is to be resolved (options.resolve is true)
    resolving: boolean;
};

type UsePromiseOptions = {
    resolve?: boolean;
};

function usePromise<T>(
    callback: () => Promise<T>,
    dependencies: DependencyList,
    options?: UsePromiseOptions,
): UsePromise<T> {
    const { resolve } = options ?? { resolve: true };
    const [result, setResult] = useState<UsePromise<T>>({
        loading: false,
        resolving: resolve,
    });

    const promiseFn = useCallback(callback, dependencies);

    useEffect(() => {
        // used to prevent setting state on an unmounted component
        let isMounted = true;

        if (!resolve) {
            setResult((prev) => ({
                ...prev,
                loading: false,
                resolving: false,
            }));
        } else if (!result.loading && resolve && !result.data && !result.error) {
            promiseFn()
                .then((data) => {
                    if (isMounted) {
                        setResult({ loading: false, data, resolving: resolve });
                    }
                })
                .catch((error) => {
                    if (isMounted) {
                        setResult({ loading: false, error, resolving: resolve });
                    }
                });
        }

        return () => {
            isMounted = false;
        };
    }, [promiseFn, resolve]);

    return result;
}

type Application = {
    id: number;
    displayName: string;
    identifier: string;
    url: URL;
};

type UseApplications = {
    loading: boolean;
    applications: Application[];
};

const useApplications = (opened: boolean): UseApplications => {
    const userManager = useUserManager();

    const applications = usePromise(
        async (): Promise<Application[]> => {
            console.debug("Fetching user-related applications");

            const user = await userManager.getUser();
            const userSub = user.profile.sub;
            if (!userSub) {
                console.error("User sub is undefined, cannot fetch applications");
                return [];
            }

            const response = await fetch(`/api/user-related-clients/${userSub}`, {
                headers: {
                    Authorization: `Bearer ${user.access_token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch applications: ${response.statusText}`);
            }
            const data = await response.json();

            const clients: Application[] = data.map(
                (client: any) =>
                    ({
                        id: client.id,
                        displayName: client.display_name,
                        identifier: client.client_id,
                        url: new URL(client.redirect_uris[0]),
                    } as Application),
            );

            return clients;
        },
        [userManager],
        { resolve: opened },
    );

    return {
        loading: applications.loading,
        applications: applications.data || [],
    };
};

const Applications = ({ opened }: { opened: boolean }) => {
    const applications = useApplications(opened);

    return (
        <IfComp condition={applications.applications.length > 0}>
            <ListSubheader>
                <FormattedMessage id="user_info.applications" />
            </ListSubheader>
            {applications.applications.map((app, i) => (
                <Item key={i} href={`${app.url.protocol}//${app.url.host}`} label={app.displayName} />
            ))}
        </IfComp>
    );
};

export default Applications;
